import { QueryClient, type QueryFunctionContext } from '@tanstack/react-query';
import { isRequestTimeoutError } from './apiClient';
import { clearFeatureFlagsCache } from './featureFlagsCache';
import { type LooseObject } from '../types/looseObject';
export { queryKeys } from './queryKeys';
export type { QueryKeys } from './queryKeys';

// Auth-aware retry: do not RQ-retry 401 (session) or 403 (permission).
// Session recovery for 401 happens in sessionAuth interceptors (refresh/replay
// or login redirect), not via React Query retries.
export const authAwareRetry = (failureCount: number, error: unknown): boolean => {
  // Client-side axios timeouts already waited API_TIMEOUT; retrying would
  // spam timeout toasts and keep spinners alive for minutes.
  if (isRequestTimeoutError(error)) {
    return false;
  }

  const status =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;

  // 401 = unauthenticated/expired session; 403 = authenticated but forbidden.
  // Neither should be retried by React Query.
  if (status === 401 || status === 403) {
    return false;
  }

  // Don't retry on client errors (4xx) except for 408, 429
  if (status !== undefined && status >= 400 && status < 500) {
    if (status === 408 || status === 429) {
      return failureCount < 2; // Retry timeout and rate limit errors
    }
    return false;
  }

  // 500 is an application error (e.g. pydantic ValidationError). Retrying it
  // turned one sharing-review failure into a 12-call burst (EPD-491).
  if (status === 500) {
    return false;
  }

  // Retry other server errors (5xx) and network errors up to 3 times
  return failureCount < 3;
};

// Create a QueryClient instance with custom configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Use auth-aware retry logic
      retry: authAwareRetry,
      // Retry with exponential backoff
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (disabled to prevent unnecessary requests with stale tokens)
      refetchOnWindowFocus: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
      // Network mode - fail fast on auth errors
      networkMode: 'online',
    },
    mutations: {
      // Use auth-aware retry for mutations too
      retry: authAwareRetry,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

// Cache management utilities for authentication scenarios
export const cacheManager = {
  // Clear all cached data on logout
  clearAllCache: () => {
    queryClient.clear();
    clearFeatureFlagsCache();
  },

  // Clear specific user-related data
  clearUserData: (userId?: string) => {
    if (userId) {
      queryClient.removeQueries({ queryKey: ['user', userId] });
    } else {
      queryClient.removeQueries({ queryKey: ['user'] });
    }
  },

  // Clear all API data but keep static data
  clearApiData: () => {
    queryClient.removeQueries({ queryKey: ['api'] });
    queryClient.removeQueries({ queryKey: ['tableData'] });
  },

  // Invalidate all queries to force refetch with new token
  invalidateAll: () => {
    queryClient.invalidateQueries();
  },

  // Check if cache should be cleared based on Auth0 authentication state
  handleAuthStateChange: (isAuthenticated: boolean, user?: unknown) => {
    if (!isAuthenticated) {
      // User is logged out - clear everything
      queryClient.clear();
      clearFeatureFlagsCache();
      // Clear localStorage items as well
      const keys = [
        'eptChannels',
        'forumIds',
        'selectedForumId',
        'selectForumName',
        'accessToken',
        'auth0User',
      ];
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
      // Clear navigation cache (all versions and users)
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.startsWith('eptai_navigation_cache_')) {
          localStorage.removeItem(key);
        }
      });
    } else if (user) {
      // User is logged in - check if it's a different user
      const storedUserId = localStorage.getItem('auth0User');
      if (storedUserId && storedUserId !== (user as LooseObject).sub) {
        // Different user logged in - clear cache for security
        queryClient.clear();
        clearFeatureFlagsCache();
        // Clear navigation cache for the old user
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (key.startsWith('eptai_navigation_cache_')) {
            localStorage.removeItem(key);
          }
        });
        console.log('Different user detected, clearing cache');
      }
    }
  },

  // Handle token expiry (legacy method for backward compatibility)
  handleTokenExpiry: () => {
    queryClient.clear();
    clearFeatureFlagsCache();
    const keys = [
      'eptChannels',
      'forumIds',
      'selectedForumId',
      'selectForumName',
      'accessToken',
      'auth0User',
    ];
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
    // Clear navigation cache
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (key.startsWith('eptai_navigation_cache_')) {
        localStorage.removeItem(key);
      }
    });
  },

  // Auth-aware query function that checks authentication before making requests.
  // Forwards React Query's AbortSignal so abandoned fetches cancel instead of
  // timing out and toasting on whatever page is active.
  createAuthAwareQueryFn: <T>(
    queryFn: (context: QueryFunctionContext) => Promise<T>,
    isAuthenticated: boolean
  ) => {
    return async (context: QueryFunctionContext): Promise<T> => {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }
      return queryFn(context);
    };
  },
};
